import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MultiSelect } from '@progress/kendo-react-dropdowns';
import { filterBy } from '@progress/kendo-data-query';
import { FluentField } from "./Fluent-Field";
import './fluent-overide.scss';

// TAD-5744 — Kendo React's MultiSelect does NOT filter its `data` automatically
// when `filterable` is set; the consumer must narrow the data in response to
// `onFilterChange` (the same pattern the form-automation dropdown-cell
// components already use via `filterBy`). Previously this component's
// `onFilterChange` only scrolled the focused item into view and ignored the
// typed text, so the Sub-Department(s) list never filtered as the user typed.
//
// This helper returns the options that are BOTH (a) not already selected and
// (b) match the current search filter. With no active filter it returns the
// full not-selected list (the prior behavior).
const getAvailableOptions = (allOptions, selectedOptions, activeFilter) => {
  if (!allOptions?.length) return [];
  const notSelected = allOptions.filter(
    option => !selectedOptions.some(selected => selected.id === option.id)
  );
  return activeFilter && activeFilter.value
    ? filterBy(notSelected, activeFilter)
    : notSelected;
};

export const FluentMultiselectList = ({
  placeholder,
  options,
  label,
  required,
  onChange,
  selectedValues,
  allowCustom = false,
  displayLabel = true,
  loading = false,
  disabled = false,
  skipDisabledItems = false,
  rounded = "large",
  isFluent = true,
}) => {
  const [selectedOptions, setSelectedOptions] = useState(selectedValues || []);
  const [filteredOptions, setFilteredOptions] = useState(options || []);
  // TAD-5744 — current search filter from the MultiSelect's filter input.
  const [filter, setFilter] = useState(null);

  useEffect(() => {
    if (selectedValues?.length > 0) {
      setSelectedOptions(selectedValues);
    }
  }, [selectedValues]);

  // TAD-5744 — recompute the visible list whenever the options, the selection,
  // or the search text changes: exclude already-selected items AND apply the
  // typed search filter.
  useEffect(() => {
    setFilteredOptions(getAvailableOptions(options, selectedOptions, filter));
  }, [options, selectedOptions, filter]);

  const isObjectOptions = (options?.length > 0 && typeof options[0] === 'object') || (selectedValues?.length > 0 && typeof selectedValues[0] === 'object');


  const handleChange = (event) => {
    const newSelectedOptions = event.target.value;
    setSelectedOptions(newSelectedOptions);
    // TAD-5744 — Kendo clears its filter input on selection; reset our filter
    // too so the remaining options aren't left narrowed by stale search text.
    // The visible list is recomputed by the effect above.
    setFilter(null);

    if (onChange) {
      onChange(newSelectedOptions);
    }
  };

  // TAD-5744 — capture the typed search text so the list filters as the user
  // types (and keep the existing scroll-into-view behavior).
  const handleFilterChange = (event) => {
    setFilter(event.filter);
    setTimeout(scrollFocusedIntoView, 0);
  };

  const scrollFocusedIntoView = () => {
    const focused =
      document.querySelector('.k-animation-container .k-popup .k-focus') ||
      document.querySelector('.k-popup .k-focus') ||
      document.querySelector('.k-focus');

    if (focused && typeof focused.scrollIntoView === 'function') {
      focused.scrollIntoView({ block: 'nearest' });
    }
  };

  // TAD-5744 — Keyboard selection. Kendo natively handles the arrow keys
  // (it highlights the focused list item with `.k-focus` and sets the input's
  // aria-activedescendant), but in this configuration it does NOT select the
  // highlighted item when Enter is pressed — so the list could be navigated by
  // keyboard but not chosen with it. This handler selects the currently
  // highlighted item on Enter, mirroring a mouse click on it.
  //
  // NOTE: it's attached to the wrapper <div>, not the MultiSelect — Kendo's
  // MultiSelect does not forward an `onKeyDown` prop (it was never invoked),
  // but keydowns from the input bubble up to the wrapper where we catch them.
  //
  // It reads Kendo's own `.k-focus` item (rather than tracking a separate
  // focus index) so it stays in sync with Kendo's navigation for every key
  // (Arrow/Home/End/PageUp/Down). The index of that item in the popup matches
  // its index in `filteredOptions` (the popup renders a flat list in order).
  // The add is idempotent (skips items already selected), so it remains safe
  // even if a future Kendo build also selects natively on Enter.
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      const scope =
        document.querySelector('.k-animation-container .k-popup') ||
        document.querySelector('.k-popup') ||
        document;
      const items = Array.from(scope.querySelectorAll('.k-list-item'));
      const focusedIndex = items.findIndex(item =>
        item.classList.contains('k-focus')
      );

      if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
        const option = filteredOptions[focusedIndex];
        const alreadySelected = selectedOptions.some(
          selected => selected.id === option.id
        );
        if (!alreadySelected) {
          const newSelectedOptions = [...selectedOptions, option];
          setSelectedOptions(newSelectedOptions);
          // Match handleChange: clear the search so the list isn't left filtered.
          setFilter(null);
          if (onChange) {
            onChange(newSelectedOptions);
          }
        }
        // Stop Enter from bubbling to a surrounding form (avoids an
        // accidental submit when the user is just picking an item).
        event.preventDefault?.();
      }
    }

    setTimeout(scrollFocusedIntoView, 0);
  };

  const containerClass = isFluent
    ? 'custom-multiselect-container-fluent'
    : 'custom-multiselect-container';

  const popupClass = isFluent
    ? 'custom-multiselect-popup-fluent'
    : 'custom-multiselect-popup';

  const renderMultiSelect = () => (
    <MultiSelect
      data={filteredOptions}
      value={selectedOptions}
      placeholder={placeholder}
      onChange={handleChange}
      allowCustom={allowCustom}
      size="medium"
      rounded={rounded}
      textField={isObjectOptions ? 'name' : undefined}
      dataItemKey={isObjectOptions ? 'id' : undefined}
      loading={loading}
      disabled={disabled}
      autoClose={false}
      skipDisabled={skipDisabledItems}
      noDataTemplate="No Option Available"
      filterable
      onFilterChange={handleFilterChange}
      onOpen={() => setTimeout(scrollFocusedIntoView, 0)}
      popupSettings={{ className: popupClass }}
    />
  );

  return (
    // TAD-5744 — keydown handled here on the wrapper: Kendo's MultiSelect does
    // not forward an onKeyDown prop, but keydowns from its input bubble up to
    // this element, letting us select the arrow-highlighted item on Enter.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className={containerClass} onKeyDown={handleKeyDown}>
      {displayLabel ? (
        <FluentField
          label={label}
          required={required}
        >
          {renderMultiSelect()}
        </FluentField>
      ) : (
        renderMultiSelect()
      )}
    </div>
  );
};

FluentMultiselectList.propTypes = {
  placeholder: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ])
  ),
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  onChange: PropTypes.func,
  selectedValues: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
    ])
  ),
  allowCustom: PropTypes.bool,
  displayLabel: PropTypes.bool,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  skipDisabledItems: PropTypes.bool,
  rounded: PropTypes.oneOf(['small', 'medium', 'large']),
  isFluent: PropTypes.bool,
};

FluentMultiselectList.defaultProps = {
  required: false,
  onChange: null,
  selectedValues: [],
  displayLabel: true,
  loading: false,
  disabled: false,
};

